import type { Option } from '@ksyos/design-system';
import { DateTime } from '@ksyos/date-time';
import {
    Accordion,
    AccordionContent,
    AccordionContentIndent,
    AccordionItem,
    AccordionTrigger,
    Box,
    Button,
    EmptyState,
    FormGroup,
    Link,
    Loader,
    PageHeader,
    Select,
    TextInput,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IFundusNoShowGeneralPractitioner } from '@common/utils/fundusNoShowGeneralPractitioner';
import { ManagerAuthorization } from '@common/enums';
import { formatString } from '@common/utils/formatString';
import { UrlBuilder } from '@common/utils/urlBuilder';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import { hasOneAuthorization } from '@frontend/utils';
import { PageTemplate } from '../../templates/PageTemplate';
import { MonthAccordion } from '../requests/requestsList/RequestsListMonthAccordion';
import { FundusNoShowsGroup, type IFundusNoShow } from './FundusNoShowsGroup';

interface FundusNoShowsByMonth {
    month: number;
    noShows: IFundusNoShow[];
}

interface FundusNoShowsByYear {
    months: FundusNoShowsByMonth[];
    totalNoShowsInYear: number;
    year: number;
}

interface FundusNoShowsFilters {
    searchText?: string;
    selectedCareGroupName?: string;
    selectedGeneralPractitioner?: string;
}

export function FundusNoShowsList() {
    const [searchText, setSearchText] = useState<string>();
    const [selectedGeneralPractitioner, setSelectedGeneralPractitioner] = useState<string>();
    const [selectedCareGroupName, setSelectedCareGroupName] = useState<string>();
    const [fundusNoShowsByYear, setFundusNoShowsByYear] = useState<FundusNoShowsByYear[]>([]);
    const [fundusNoShowsGpOptions, setFundusNoShowsGpOptions] = useState<Option[]>();
    const [careGroupNames, setCareGroupNames] = useState<string[]>();
    const [selectedYear, setSelectedYear] = useState<number>();
    const [selectedMonth, setSelectedMonth] = useState<number>();
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        getFundusNoShows('/api/fundusScreening/noShows');
        loadFilters();
    }, []);

    const translate = getSelectedLanguage();
    const isAdmin = hasOneAuthorization(ManagerAuthorization.fundusScreeningManualImport);

    return (
        <PageTemplate>
            <PageHeader title="No shows Fundus" />

            <Box
                display="flex"
                flexWrap="wrap"
                gap={2}
            >
                <FormGroup
                    label={translate.common.searchTerm}
                    layout="vertical"
                    sx={{ minWidth: '35%' }}
                >
                    <TextInput
                        onChange={async (e) => {
                            setSearchText(e.target.value);
                            await search({
                                searchText: e.target.value,
                                selectedCareGroupName,
                                selectedGeneralPractitioner,
                            });
                        }}
                        onEnterKeyPress={async () =>
                            await search({
                                searchText,
                                selectedCareGroupName,
                                selectedGeneralPractitioner,
                            })
                        }
                        placeholder={translate.common.searchByNameBsnBirthdate}
                        size="large"
                        suffix={{
                            icon: 'search',
                            labelText: 'search icon',
                            onSuffixClick: () => {
                                (async () => {
                                    await search({
                                        searchText,
                                        selectedCareGroupName,
                                        selectedGeneralPractitioner,
                                    });
                                })();
                            },
                        }}
                        value={searchText ?? ''}
                        variant="outline"
                    />
                </FormGroup>

                {isAdmin && (
                    <FormGroup
                        label={translate.fundusNoShows.filterOnCareGroup}
                        layout="vertical"
                        sx={{ minWidth: '18%' }}
                    >
                        <Select
                            isClearable={true}
                            onChange={async (option) => {
                                setSelectedCareGroupName(option?.value);
                                await search({
                                    searchText,
                                    selectedCareGroupName: option?.value,
                                    selectedGeneralPractitioner,
                                });
                            }}
                            options={careGroupNames?.map((careGroupName) => {
                                return {
                                    label: careGroupName,
                                    value: careGroupName,
                                };
                            })}
                            size="large"
                            value={
                                selectedCareGroupName
                                    ? {
                                          label: selectedCareGroupName,
                                          value: selectedCareGroupName,
                                      }
                                    : null
                            }
                        />
                    </FormGroup>
                )}
                <FormGroup
                    label={translate.fundusNoShows.filterOnGeneralPractitoner}
                    layout="vertical"
                    sx={{ minWidth: '18%' }}
                >
                    <Select
                        isClearable={true}
                        onChange={async (option) => {
                            setSelectedGeneralPractitioner(option?.value);
                            await search({
                                searchText,
                                selectedCareGroupName,
                                selectedGeneralPractitioner: option?.value,
                            });
                        }}
                        options={fundusNoShowsGpOptions}
                        size="large"
                        value={
                            fundusNoShowsGpOptions?.find((option) => option.value === selectedGeneralPractitioner) ??
                            null
                        }
                    />
                </FormGroup>
                <FormGroup
                    layout="vertical"
                    sx={{ marginTop: ['2.5rem'] }}
                >
                    <Link
                        color="subtle"
                        fontWeight="normal"
                        onClick={clearFilters}
                        variant="button"
                    >
                        {translate.common.deleteAll}
                    </Link>
                </FormGroup>
            </Box>

            {isLoading ? (
                <Loader
                    className="mt-3 mx-auto"
                    size="large"
                />
            ) : (
                <>
                    <Accordion
                        collapsible={true}
                        defaultValue={selectedYear?.toString()}
                        onValueChange={(value) => {
                            setSelectedYear(value ? Number(value) : undefined);
                            setSelectedMonth(undefined);
                        }}
                        type="single"
                    >
                        {fundusNoShowsByYear?.map((yearData) => {
                            const monthCountsForAccordion: Record<number, number> = {};
                            yearData.months.forEach((monthObj) => {
                                monthCountsForAccordion[monthObj.month] = monthObj.noShows.length;
                            });
                            const groupedListForMonthAccordion = {
                                [yearData.year.toString()]: monthCountsForAccordion,
                            };

                            return (
                                <AccordionItem
                                    key={yearData.year}
                                    value={yearData.year.toString()}
                                >
                                    <AccordionTrigger icon="calender">
                                        {yearData.year}
                                        <Box
                                            color="neutral400"
                                            ml={2}
                                        >
                                            ({yearData.totalNoShowsInYear})
                                        </Box>
                                    </AccordionTrigger>
                                    <AccordionContent>
                                        <AccordionContentIndent>
                                            {selectedYear === yearData.year && (
                                                <MonthAccordion
                                                    groupedList={groupedListForMonthAccordion}
                                                    month={selectedMonth || null}
                                                    setMonth={(monthNumber) => setSelectedMonth(monthNumber)}
                                                    year={yearData.year}
                                                >
                                                    <FundusNoShowsGroup
                                                        fundusNoShows={
                                                            yearData.months.find((m) => m.month === selectedMonth)
                                                                ?.noShows || []
                                                        }
                                                        isAdmin={isAdmin}
                                                        key={`${yearData.year}-${selectedMonth}`}
                                                        onRemoveNoShow={removeNoShow}
                                                    />
                                                </MonthAccordion>
                                            )}
                                        </AccordionContentIndent>
                                    </AccordionContent>
                                </AccordionItem>
                            );
                        })}
                    </Accordion>

                    {!fundusNoShowsByYear?.length && (
                        <div style={{ marginTop: '1rem' }}>
                            <EmptyState
                                icon="searchresults"
                                subtitle={getNoResultsSubtitle()}
                                title={
                                    searchText
                                        ? formatString(translate.fundusNoShows.resultsNotFound, [searchText])
                                        : translate.fundusNoShows.thereAreCurrentlyNoFundusNoShows
                                }
                                variant="centered"
                            >
                                {searchText && (
                                    <Button
                                        color="primary"
                                        onClick={clearFilters}
                                        variant="outline"
                                    >
                                        {translate.common.deleteAll}
                                    </Button>
                                )}
                            </EmptyState>
                        </div>
                    )}
                </>
            )}
        </PageTemplate>
    );

    async function getFundusNoShows(url: string) {
        setIsLoading(true);
        const result = await fetchJson(url, 'GET');
        const noShowsResult: IFundusNoShow[] = result?.json?.items ?? [];

        const noShowsByYearAndMonth: Record<number, Record<number, IFundusNoShow[]>> = {};

        for (const noShow of noShowsResult) {
            const dateTime = DateTime.from(noShow.period);
            const year = dateTime.year();
            const month = dateTime.month();

            if (!noShowsByYearAndMonth[year]) {
                noShowsByYearAndMonth[year] = {};
            }
            if (!noShowsByYearAndMonth[year][month]) {
                noShowsByYearAndMonth[year][month] = [];
            }
            noShowsByYearAndMonth[year][month].push(noShow);
        }

        const processedNoShowsByYear: FundusNoShowsByYear[] = Object.keys(noShowsByYearAndMonth)
            .map((yearStr) => Number(yearStr))
            .sort((a, b) => b - a)
            .map((year) => {
                let totalInYear = 0;
                const monthsData: FundusNoShowsByMonth[] = Object.keys(noShowsByYearAndMonth[year])
                    .map((monthStr) => Number(monthStr))
                    .sort((a, b) => a - b)
                    .map((monthNumber) => {
                        const noShowsForMonth = noShowsByYearAndMonth[year][monthNumber];
                        totalInYear += noShowsForMonth.length;

                        return {
                            month: monthNumber,
                            noShows: noShowsForMonth,
                        };
                    });

                return {
                    months: monthsData,
                    totalNoShowsInYear: totalInYear,
                    year: year,
                };
            });

        setFundusNoShowsByYear(processedNoShowsByYear);
        setMostRecentYearAndMonth(processedNoShowsByYear);
        setIsLoading(false);
    }

    async function loadGeneralPractitionersOfNoShowPatients() {
        const result = await fetchJson('/api/fundusScreening/noShows/getGeneralPractitioners', 'GET');
        const practitioners: IFundusNoShowGeneralPractitioner[] = result?.json?.items ?? [];
        if (practitioners.length) {
            const options = practitioners.map((practitioner) => {
                return {
                    label: practitioner.nameGiven + ' ' + practitioner.nameFamily,
                    value: practitioner.id,
                };
            });

            setFundusNoShowsGpOptions(options);
        }
    }

    async function loadCareGroupNames() {
        const result = await fetchJson('/api/fundusScreening/noShows/getCareGroupNames', 'GET');
        setCareGroupNames(result.json.careGroupNames);
    }

    async function search(filters: FundusNoShowsFilters) {
        const urlBuilder = new UrlBuilder('/api/fundusScreening/noShows');

        if (filters.searchText) {
            urlBuilder.add('searchText', filters.searchText);
        }

        if (filters.selectedGeneralPractitioner) {
            urlBuilder.add('selectedGeneralPractitioner', filters.selectedGeneralPractitioner);
        }

        if (filters.selectedCareGroupName) {
            urlBuilder.add('selectedCareGroupName', filters.selectedCareGroupName);
        }

        await getFundusNoShows(urlBuilder.build());
    }

    async function clearFilters() {
        setSearchText(undefined);
        setSelectedGeneralPractitioner(undefined);
        setSelectedCareGroupName(undefined);
        setSelectedMonth(undefined);
        setSelectedYear(undefined);
        await getFundusNoShows('/api/fundusScreening/noShows');
    }

    async function loadFilters() {
        await loadGeneralPractitionersOfNoShowPatients();
        if (isAdmin) {
            await loadCareGroupNames();
        }
    }

    async function removeNoShow(fundusNoShow: IFundusNoShow) {
        const url = `/api/fundusScreening/noShows/${fundusNoShow.id}`;
        const result = await fetchJson(url, 'POST', { isRemoved: true });

        if (result && result.json.id === fundusNoShow.id) {
            await getFundusNoShows('/api/fundusScreening/noShows');
            await loadFilters();
        }
    }

    function getNoResultsSubtitle() {
        if (searchText) {
            return translate.fundusNoShows.checkSearchTerm;
        } else {
            return isAdmin
                ? translate.fundusNoShows.importFileToSeeNoShows
                : translate.fundusNoShows.noFundusShowsSubtible;
        }
    }

    function setMostRecentYearAndMonth(noShows: FundusNoShowsByYear[]) {
        if (noShows.length) {
            const mostRecentFundusNoShowByYear = noShows[0];
            setSelectedYear(mostRecentFundusNoShowByYear.year);

            const mostRecentMonth = [...mostRecentFundusNoShowByYear.months].sort((a, b) => b.month - a.month)[0]
                ?.month;
            setSelectedMonth(mostRecentMonth);
        } else {
            setSelectedYear(undefined);
            setSelectedMonth(undefined);
        }
    }
}
